import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SpectatorRequest.css';
import logger from '../utils/logger';

const SpectatorRequestPending = ({ gameId, onApproved, onDenied, onCancel }) => {
  const [status, setStatus] = useState('pending'); // pending, approved, denied
  const [countdown, setCountdown] = useState(3);
  const wsRef = useRef(null);
  const { token, user } = useAuth();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!gameId || !token || !user) return;

    // Connect to game WebSocket to listen for approval/denial
    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/game/${gameId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      // Authenticate
      ws.send(JSON.stringify({
        type: 'auth',
        user_id: user.user_id,
        is_spectator: true
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'spectator_approved' && data.user_id === user.user_id) {
          setStatus('approved');
        } else if (data.type === 'spectator_denied' && data.user_id === user.user_id) {
          setStatus('denied');
        }
      } catch (error) {
        logger.error('Error parsing message:', error);
      }
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error:', error);
    };

    ws.onclose = () => {
    };

    // Poll for status changes (backup in case WebSocket misses)
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/multiplayer/spectate/${gameId}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'approved') {
            setStatus('approved');
          } else if (data.status === 'denied') {
            setStatus('denied');
          }
        }
      } catch (error) {
        logger.warn('Spectator status poll error:', error.message);
      }
    }, 2000);

    return () => {
      clearInterval(pollInterval);
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, [gameId, token, user, API_URL]);

  // Handle countdown and redirect
  useEffect(() => {
    if (status === 'approved' || status === 'denied') {
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            if (status === 'approved' && onApproved) {
              onApproved();
            } else if (status === 'denied' && onDenied) {
              onDenied();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, onApproved, onDenied]);

  const handleCancel = async () => {
    try {
      // Remove the pending request
      await fetch(`${API_URL}/api/multiplayer/spectate/${gameId}/cancel-request`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      logger.error('Error canceling request:', error);
    }
    
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="spectator-request-overlay" data-testid="spectator-request-pending">
      <div className="spectator-request-modal">
        {status === 'pending' && (
          <>
            <div className="request-icon pending">
              <div className="spinner"></div>
            </div>
            <h2>Request Pending</h2>
            <p>Waiting for the players to approve your request to spectate...</p>
            <p className="hint">The game host will see your request and can accept or deny it.</p>
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel Request
            </button>
          </>
        )}

        {status === 'approved' && (
          <>
            <div className="request-icon approved">
              <span>✓</span>
            </div>
            <h2>Request Approved!</h2>
            <p>The players have approved your spectator request.</p>
            <button 
              className="enter-game-btn"
              onClick={() => {
                if (onApproved) onApproved();
              }}
              data-testid="spectator-enter-btn"
            >
              🎮 Enter Game
            </button>
            <p className="redirect-notice">Auto-entering in {countdown}...</p>
          </>
        )}

        {status === 'denied' && (
          <>
            <div className="request-icon denied">
              <span>✗</span>
            </div>
            <h2>Request Denied</h2>
            <p>The players have denied your spectator request.</p>
            <p className="redirect-notice">Returning to lobby in {countdown}...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SpectatorRequestPending;
